// src/Widerruf.js — AVS Cloud | §356a BGB Widerrufsbutton | Stand: Juni 2026
// ÖFFENTLICHE SEITE — Kein Login erforderlich
import React, { useState, useEffect } from 'react';
import { setPageMeta } from './utils/seo';

const s = {
  wrap: { minHeight: '100vh', padding: '2rem clamp(1rem, 4vw, 2.5rem) 5rem', position: 'relative', zIndex: 1 },
  inner: { maxWidth: '640px', margin: '0 auto' },
  back: { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.04em', textDecoration: 'none', marginBottom: '2.5rem', cursor: 'pointer' },
  title: { fontFamily: 'var(--font-head)', fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 900, letterSpacing: '-0.04em', color: 'var(--white)', marginBottom: '0.4rem', lineHeight: 1.1 },
  subtitle: { fontSize: '0.82rem', color: 'var(--muted)', marginBottom: '2.5rem', letterSpacing: '0.04em', paddingBottom: '2rem', borderBottom: '1px solid var(--border)' },
  p: { fontSize: '0.88rem', color: 'var(--muted)', lineHeight: 1.8, margin: 0 },
  pmt: { fontSize: '0.88rem', color: 'var(--muted)', lineHeight: 1.8, marginTop: '0.75rem' },
  link: { color: 'var(--cyan)', textDecoration: 'underline', textUnderlineOffset: '3px' },
  strong: { color: 'var(--white)', fontWeight: 600 },
  infoBox: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid var(--border)',
    borderRadius: '10px',
    padding: '1.5rem',
    marginBottom: '2rem',
  },
  widerrufBtn: {
    display: 'block',
    width: '100%',
    padding: '0.9rem 1.5rem',
    background: 'rgba(0, 212, 255, 0.1)',
    border: '1px solid rgba(0, 212, 255, 0.4)',
    borderRadius: '8px',
    color: 'var(--cyan)',
    fontFamily: 'var(--font-head)',
    fontWeight: 700,
    fontSize: '1rem',
    cursor: 'pointer',
    letterSpacing: '0.02em',
    marginTop: '1.5rem',
    transition: 'all 0.2s',
  },
  form: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid var(--border)',
    borderRadius: '10px',
    padding: '1.5rem',
    marginTop: '1.5rem',
  },
  label: {
    display: 'block',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--muted)',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom: '0.4rem',
  },
  input: {
    display: 'block',
    width: '100%',
    padding: '0.7rem 1rem',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid var(--border)',
    borderRadius: '6px',
    color: 'var(--white)',
    fontSize: '0.88rem',
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: '1rem',
  },
  hint: { fontSize: '0.78rem', color: 'var(--muted)', marginTop: '-0.75rem', marginBottom: '1rem', lineHeight: 1.5 },
  confirmBtn: {
    display: 'block',
    width: '100%',
    padding: '0.85rem 1.5rem',
    background: 'rgba(255, 80, 80, 0.12)',
    border: '1px solid rgba(255, 80, 80, 0.4)',
    borderRadius: '8px',
    color: '#ff6b6b',
    fontFamily: 'var(--font-head)',
    fontWeight: 700,
    fontSize: '0.95rem',
    cursor: 'pointer',
    letterSpacing: '0.02em',
    marginTop: '0.5rem',
    transition: 'all 0.2s',
  },
  confirmBtnDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  successBox: {
    background: 'rgba(0, 200, 120, 0.08)',
    border: '1px solid rgba(0, 200, 120, 0.3)',
    borderRadius: '10px',
    padding: '1.5rem',
    marginTop: '1.5rem',
  },
  errorBox: {
    background: 'rgba(255, 80, 80, 0.08)',
    border: '1px solid rgba(255, 80, 80, 0.3)',
    borderRadius: '8px',
    padding: '1rem 1.25rem',
    marginTop: '1rem',
    fontSize: '0.85rem',
    color: '#ff8888',
  },
  noticeBox: {
    background: 'rgba(255, 200, 0, 0.05)',
    border: '1px solid rgba(255, 200, 0, 0.25)',
    borderRadius: '8px',
    padding: '1rem 1.25rem',
    marginTop: '2rem',
    fontSize: '0.82rem',
    color: 'var(--muted)',
    lineHeight: 1.7,
  },
};

// Formatiert Datum auf deutsches Format: TT.MM.JJJJ, HH:MM Uhr MEZ/MESZ
function formatDe(isoString) {
  const d = new Date(isoString);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())} Uhr`;
}

function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function Widerruf() {
  useEffect(() => {
    setPageMeta({
      title: 'Widerruf | AVS Cloud',
      description: 'Widerrufsrecht für AVS Cloud-Abonnements gemäß §356a BGB. Widerruf online innerhalb von 14 Tagen einreichen.',
      path: '/widerruf',
    });
  }, []);
  const [step, setStep] = useState(1); // 1 = Info, 2 = Formular, 3 = Erfolg
  const [email, setEmail] = useState('');
  const [contractRef, setContractRef] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [receivedAt, setReceivedAt] = useState('');

  const canSubmit =
    validateEmail(email) &&
    contractRef.trim().length >= 3 &&
    !loading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/billing/widerruf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          contract_ref: contractRef.trim(),
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Fehler beim Übermitteln. Bitte versuche es erneut oder sende eine E-Mail.');
        setLoading(false);
        return;
      }

      setReceivedAt(data.received_at);
      setStep(3);
    } catch (err) {
      setError('Netzwerkfehler. Bitte versuche es erneut oder sende eine E-Mail an aquavservices@gmail.com.');
      setLoading(false);
    }
  };

  return (
    <div style={s.wrap}>
      <div style={s.inner}>
        <a href="/" style={s.back}>← Zurück zur Cloud</a>
        <h1 style={s.title}>Vertrag widerrufen</h1>
        <p style={s.subtitle}>
          Gemäß § 356a BGB — Widerrufsbutton für Fernabsatzverträge
        </p>

        {/* Info-Box */}
        <div style={s.infoBox}>
          <p style={{ ...s.p, color: 'var(--white)', fontWeight: 600, marginBottom: '0.75rem' }}>
            Was ist der Unterschied zwischen Widerruf und Kündigung?
          </p>
          <p style={s.p}>
            <span style={s.strong}>Widerruf:</span> Gilt nur innerhalb von 14 Tagen
            nach Vertragsschluss und macht den Vertrag rückwirkend unwirksam
            (§ 355 BGB). Bereits gezahlte Beträge werden anteilig erstattet.
          </p>
          <p style={s.pmt}>
            <span style={s.strong}>Kündigung:</span> Für laufende Abonnements nach
            Ablauf der 14-Tage-Frist. Kündigung erfolgt in den{' '}
            <a href="/settings" style={s.link}>
              Einstellungen
            </a>{' '}
            zum Monatsende.
          </p>
          <p style={s.pmt}>
            Widerrufsfrist: <span style={s.strong}>14 Tage</span> ab
            Vertragsschluss (Datum in der Buchungsbestätigung).
          </p>
        </div>

        {/* STUFE 1: Widerrufsbutton */}
        {step === 1 && (
          <>
            <p style={s.p}>
              Um Ihren Widerruf einzureichen, klicken Sie auf den Button unten.
              Sie benötigen Ihre E-Mail-Adresse und die Abo-ID oder
              Rechnungsnummer aus Ihrer Buchungsbestätigung.
            </p>
            <button
              style={s.widerrufBtn}
              onClick={() => setStep(2)}
            >
              Vertrag widerrufen →
            </button>
          </>
        )}

        {/* STUFE 2: Formular */}
        {step === 2 && (
          <div style={s.form}>
            <p style={{ ...s.p, color: 'var(--white)', fontWeight: 600, marginBottom: '1.25rem' }}>
              Widerrufsformular — Schritt 2 von 2
            </p>

            <label style={s.label}>E-Mail-Adresse *</label>
            <input
              type="email"
              style={s.input}
              placeholder="deine@email.de"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />

            <label style={s.label}>Vertragsreferenz (Abo-ID / Rechnungsnummer) *</label>
            <input
              type="text"
              style={s.input}
              placeholder="z.B. AVS-2026-00001 oder sub_xxx"
              value={contractRef}
              onChange={(e) => setContractRef(e.target.value)}
            />
            <p style={s.hint}>
              Zu finden unter: Einstellungen → Speicher & Abo, oder in der
              Buchungsbestätigung per E-Mail.
            </p>

            {error && <div style={s.errorBox}>{error}</div>}

            <button
              style={{
                ...s.confirmBtn,
                ...(!canSubmit ? s.confirmBtnDisabled : {}),
              }}
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {loading ? 'Wird übermittelt…' : 'Widerruf bestätigen'}
            </button>

            <p style={{ ...s.p, marginTop: '1rem', fontSize: '0.78rem' }}>
              * Pflichtfelder. Es werden ausschließlich diese zwei Felder
              verarbeitet (Art. 5 Abs. 1 lit. c DSGVO — Datensparsamkeit).
            </p>
          </div>
        )}

        {/* STUFE 3: Erfolg */}
        {step === 3 && (
          <div style={s.successBox}>
            <p style={{ ...s.p, color: '#4ade80', fontWeight: 700, marginBottom: '0.75rem', fontSize: '1rem' }}>
              ✓ Widerruf eingegangen
            </p>
            <p style={s.p}>
              Wir haben Ihren Widerruf erhalten.
            </p>
            <p style={s.pmt}>
              <span style={s.strong}>Empfangszeit:</span>{' '}
              {receivedAt ? formatDe(receivedAt) : '—'}
            </p>
            <p style={s.pmt}>
              Eine Eingangsbestätigung wurde an{' '}
              <span style={s.strong}>{email}</span> gesendet.
            </p>
            <p style={{ ...s.pmt, color: '#fbbf24' }}>
              <span style={{ fontWeight: 600 }}>Wichtiger Hinweis:</span> Dies ist
              eine Eingangsbestätigung, keine Wirksamkeitsbestätigung. Wir prüfen
              Ihren Widerruf und melden uns bei Ihnen.
            </p>
          </div>
        )}

        {/* Hinweis-Box */}
        <div style={s.noticeBox}>
          <span style={{ color: 'var(--cyan)', fontWeight: 600 }}>Hinweis:</span>{' '}
          Diese Seite dient der Ausübung des Widerrufsrechts gemäß § 356a BGB.
          Für die{' '}
          <a href="/settings" style={s.link}>
            Kündigung
          </a>{' '}
          Ihres laufenden Abonnements nutzen Sie bitte die Funktion in den
          Einstellungen Ihres Accounts. Eine Kündigung ist jederzeit zum
          Monatsende möglich.
        </div>
      </div>
    </div>
  );
}
